"use client";

import { useRef, useState, useEffect, ComponentType, useMemo, useCallback } from "react";
import { useInView, motion, AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";
import { fallbackStyles } from "@/lib/animation-presets";
import { useMediaQuery } from "usehooks-ts";
interface LazySectionProps {
  /** The content to be rendered when the section comes into view */
  children: React.ReactNode;

  /**
   * Percentage of the element that must be visible before triggering
   * @default 0.2
   * @min 0 (triggers immediately when any part enters viewport)
   * @max 1 (waits until entire element is visible)
   * @example 0.1 (triggers when 10% visible), 0.5 (triggers when 50% visible)
   */
  threshold?: number;

  /**
   * Margin around the viewport to trigger loading before/after element enters view
   * @default "0px 0px -10% 0px"
   * @format "top right bottom left" (CSS margin format)
   * @example "100px 0px" (start loading 100px before entering viewport)
   * @example "50px 0px -20% 0px" (load 50px early, stop 20% before bottom)
   */
  rootMargin?: string;

  /**
   * Additional CSS classes to apply to the container
   * @default ""
   * @example "my-4 px-6" for margin and padding
   */
  className?: string;

  /**
   * Custom animation variants for enter/exit animations
   * @default defaultAnimationVariants (opacity 0→1, y 30→0)
   * @example { hidden: { scale: 0 }, visible: { scale: 1 } }
   */
  animationVariants?: {
    hidden: any;
    visible: any;
  };

  /**
   * Animation transition configuration (duration, easing, delay)
   * @default { duration: 0.6, ease: [0.4, 0, 0.2, 1], delay: 0.05 }
   * @example { duration: 1, ease: "easeOut", delay: 0.2 }
   */
  transition?: any;

  /**
   * Placeholder content shown while element is not in view
   * @default <div className="h-32 w-full animate-pulse rounded-lg bg-muted/20" />
   * @example <div className="h-40 bg-gray-200" /> for custom skeleton
   */
  fallback?: React.ReactNode;

  /**
   * Disable complex animations on mobile devices for better performance
   * @default true
   * When true: Uses simple opacity fade on mobile instead of transform animations
   * When false: Uses full animations on all devices (may impact mobile performance)
   */
  disableMobileAnimations?: boolean;

  /**
   * Remove component from DOM when it goes out of view (instead of keeping it loaded)
   * @default false
   * When true: Component is completely unmounted when out of view (saves memory)
   * When false: Component stays loaded once it has been viewed (better for performance)
   * Useful for heavy components like testimonials, videos, or large data sets
   */
  unmountWhenOutOfView?: boolean;

  /**
   * Custom margin for unmounting behavior (overrides rootMargin when unmounting)
   * @default undefined (uses rootMargin for both mounting and unmounting)
   * @format "top right bottom left" (CSS margin format)
   * @example "0px 0px -50px 0px" (unmount 50px after element leaves viewport)
   * @example "-100px 0px -100px 0px" (unmount 100px before/after element leaves viewport)
   * Only applies when unmountWhenOutOfView is true
   */
  unmountMargin?: string;
}
const defaultAnimationVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const mobileAnimationVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1
  }
};
const defaultTransition = {
  duration: 0.6,
  ease: [0.4, 0, 0.2, 1],
  delay: 0.05
};
const mobileTransition = {
  duration: 0.2,
  ease: "easeOut",
  delay: 0
};
export function LazySection({
  children,
  threshold = 0.2,
  rootMargin = "0px 0px -10% 0px",
  className = "",
  animationVariants = defaultAnimationVariants,
  transition = defaultTransition,
  fallback = <div className="h-32 w-full animate-pulse rounded-lg bg-muted/20" />,
  disableMobileAnimations = true,
  unmountWhenOutOfView = false,
  unmountMargin
}: LazySectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const isMobileQuery = useMediaQuery("(max-width: 768px)");

  // Single intersection observer for better performance
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [inView, setInView] = useState(false);

  // Only use the media query result after mounting to prevent hydration issues
  const isMobile = mounted ? isMobileQuery : false;
  useEffect(() => {
    setMounted(true);
  }, []);

  // Optimized intersection observer
  useEffect(() => {
    if (!mounted || !containerRef.current) return;
    const element = containerRef.current;

    // Create single observer for both mount and unmount detection
    observerRef.current = new IntersectionObserver(([entry]) => {
      const isIntersecting = entry.isIntersecting;
      setInView(isIntersecting);
      if (isIntersecting) {
        setIsVisible(true);
      }

      // Handle unmounting behavior
      if (unmountWhenOutOfView && !isIntersecting && hasAnimated) {
        setIsVisible(false);
      }
    }, {
      threshold: threshold,
      rootMargin: rootMargin
    });
    observerRef.current.observe(element);
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [mounted, threshold, rootMargin, unmountWhenOutOfView, hasAnimated]);

  // Determine render state based on visibility and unmount behavior
  const shouldRender = unmountWhenOutOfView ? isVisible : inView || hasAnimated;

  // Use mobile-optimized variants and transition on mobile
  const effectiveVariants = useMemo(() => {
    return isMobile && disableMobileAnimations ? mobileAnimationVariants : animationVariants;
  }, [isMobile, disableMobileAnimations, animationVariants]);
  const effectiveTransition = useMemo(() => {
    return isMobile && disableMobileAnimations ? mobileTransition : transition;
  }, [isMobile, disableMobileAnimations, transition]);

  // Animation complete handler
  const handleAnimationComplete = useCallback(() => {
    setHasAnimated(true);
  }, []);

  // Don't use animations until mounted to prevent hydration issues
  if (!mounted) {
    return <div ref={containerRef} className={className}>
        {shouldRender ? children : fallback}
      </div>;
  }
  return <div ref={containerRef} className={className} data-sentry-component="LazySection" data-sentry-source-file="lazy-section.tsx">
      <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="lazy-section.tsx">
        {shouldRender ? <motion.div key="content" initial="hidden" animate="visible" exit="hidden" variants={effectiveVariants} transition={effectiveTransition} onAnimationComplete={handleAnimationComplete}>
            {children}
          </motion.div> : <motion.div key="fallback" initial="hidden" animate="visible" exit="hidden" variants={effectiveVariants} transition={effectiveTransition}>
            {fallback}
          </motion.div>}
      </AnimatePresence>
    </div>;
}

// Higher-order component for lazy loading dynamic components
interface LazyComponentWrapperProps {
  loader: () => Promise<{
    default: ComponentType<any>;
  }>;
  threshold?: number;
  rootMargin?: string;
  className?: string;
  animationVariants?: any;
  transition?: any;
  fallback?: React.ReactNode;
  componentProps?: any;
}
export function LazyComponentWrapper({
  loader,
  threshold = 0.2,
  rootMargin = "0px 0px -10% 0px",
  className = "",
  animationVariants = defaultAnimationVariants,
  transition = defaultTransition,
  fallback,
  componentProps = {}
}: LazyComponentWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Use single intersection observer
  const isInView = useInView(containerRef, {
    once: true,
    amount: threshold,
    margin: rootMargin as any
  });

  // Dynamically load the component only when it comes into view
  const DynamicComponent = useMemo(() => {
    if (isInView && !hasLoaded && mounted) {
      return dynamic(loader, {
        loading: () => fallback ? <div>{fallback}</div> : <div className="h-32 w-full animate-pulse rounded-lg bg-muted/20" />,
        ssr: false
      });
    }
    return null;
  }, [isInView, hasLoaded, mounted, loader, fallback]);
  if (!mounted) {
    return <div ref={containerRef} className={className} />;
  }
  return <div ref={containerRef} className={className} data-sentry-component="LazyComponentWrapper" data-sentry-source-file="lazy-section.tsx">
      <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="lazy-section.tsx">
        {isInView && DynamicComponent && <motion.div initial="hidden" animate="visible" variants={animationVariants} transition={transition} onAnimationStart={() => setHasLoaded(true)}>
            <DynamicComponent {...componentProps} />
          </motion.div>}
      </AnimatePresence>
    </div>;
}